import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { deleteTopic } from "./deleteTopicSlice"
import { getPostStatusId } from "./topicsSlice"

const getMonthDates = (date = new Date()) => {
  const year = date.getFullYear()
  const month = date.getMonth()

  const startDate = new Date(year, month, 1)
  const endDate = new Date(year, month + 1, 0)

  return {
    start_date: startDate.toISOString().split("T")[0], // yyyy-MM-dd format
    end_date: endDate.toISOString().split("T")[0], // yyyy-MM-dd format
  }
}

const getYearDates = (date = new Date()) => {
  const year = date.getFullYear()

  const startDate = new Date(year, 0, 1) // January 1st
  const endDate = new Date(year, 11, 31) // December 31st

  return {
    start_date: startDate.toISOString().split("T")[0], // yyyy-MM-dd format
    end_date: endDate.toISOString().split("T")[0], // yyyy-MM-dd format
  }
}

export const fetchPublisherTopics = createAsyncThunk(
  "publisher/fetchTopics",
  async ({ clientId, date, isYearlyCall = false }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token
      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const targetDate = date || new Date()
      const { start_date, end_date } = isYearlyCall ? getYearDates(targetDate) : getMonthDates(targetDate)

      const queryParams = new URLSearchParams({
        start_date,
        end_date,
      })

      const data = await get(`topics/publisher/${clientId}?${queryParams.toString()}`)

      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch publisher topics")
    }
  },
)

const initialState = {
  topics: [],
  processedTopics: {
    draft: [],
    scheduled: [],
    live: [],
  },
  statusCounts: {
    draft: 0,
    scheduled: 0,
    live: 0,
  },
  status: "idle", // idle | loading | succeeded | failed
  error: null,
}

const publisherSlice = createSlice({
  name: "publisher",
  initialState,
  reducers: {
    clearPublisherTopics: (state) => {
      state.topics = []
      state.processedTopics = {
        draft: [],
        scheduled: [],
        live: [],
      }
      state.statusCounts = {
        draft: 0,
        scheduled: 0,
        live: 0,
      }
      state.status = "idle"
      state.error = null
    },
    clearPublisherError: (state) => {
      state.error = null
    },
    resetPublisherOnLogout: (state) => {
      return initialState
    },
    movePostToTab: (state, action) => {
      const { postId, targetTab, campaignId } = action.payload

      const allTabs = ["draft", "scheduled", "live"]

      for (const tab of allTabs) {
        const campaign = state.processedTopics[tab]?.find((c) => c.id === campaignId)
        if (!campaign) continue

        const postIndex = campaign.contentTopicPosts?.findIndex((p) => p.id === postId)
        if (postIndex === -1) continue

        // Extract the single post
        const [movedPost] = campaign.contentTopicPosts.splice(postIndex, 1)

        // Update post status based on target tab
        let newStatusId = 1 // DRAFT
        if (targetTab === "scheduled") newStatusId = 6
        if (targetTab === "live") newStatusId = 7

        movedPost.contentPostStatus = {
          ...movedPost.contentPostStatus,
          id: newStatusId,
        }

        // Remove empty campaign
        if (campaign.contentTopicPosts.length === 0) {
          state.processedTopics[tab] = state.processedTopics[tab].filter((c) => c.id !== campaignId)
          state.statusCounts[tab] = Math.max(0, (state.statusCounts[tab] || 1) - 1)
        }

        // Add post to target tab campaign
        const targetCampaign = state.processedTopics[targetTab]?.find((c) => c.id === campaignId)
        if (targetCampaign) {
          targetCampaign.contentTopicPosts.push(movedPost)
        } else {
          const newCampaign = {
            ...campaign,
            contentTopicPosts: [movedPost],
            firstPost: movedPost,
            status: targetTab,
          }
          state.processedTopics[targetTab].push(newCampaign)
          state.statusCounts[targetTab] = (state.statusCounts[targetTab] || 0) + 1
        }

        break
      }
    },
    removePostFromCampaign: (state, action) => {
      const { postId, campaignId, tabType } = action.payload
      // Find the campaign in the specified tab
      const campaigns = state.processedTopics[tabType]
      const campaignIndex = campaigns.findIndex((c) => c.id === campaignId)

      if (campaignIndex === -1) {
        console.error("Campaign not found:", campaignId)
        return
      }

      const campaign = campaigns[campaignIndex]

      // Remove the post from contentTopicPosts
      const postIndex = campaign.contentTopicPosts.findIndex((p) => p.id === postId)
      if (postIndex !== -1) {
        campaign.contentTopicPosts.splice(postIndex, 1)
      }

      // If no posts left, remove the entire campaign
      if (campaign.contentTopicPosts.length === 0) {
        campaigns.splice(campaignIndex, 1)
        state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
      } else {
        // Update firstPost to the first remaining post
        campaign.firstPost = campaign.contentTopicPosts[0]
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublisherTopics.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchPublisherTopics.fulfilled, (state, action) => {
        state.status = "succeeded"
        const data = action.payload
        state.topics = data

        const processedTopics = {
          draft: [],
          scheduled: [],
          live: [],
        }

        const statusCounts = {
          draft: 0,
          scheduled: 0,
          live: 0,
        }

        data.forEach((topic) => {
          if (topic.contentTopicPosts && topic.contentTopicPosts.length > 0) {
            const firstPost = topic.contentTopicPosts[0]
            const statusId = getPostStatusId(firstPost)

            let category = null

            switch (statusId) {
              case 1: // DRAFT
              case 2: // RETURNED
              case 3: // REJECTED
              case 5: // SENT_TO_CREATOR
                category = "draft"
                break
              case 6: // SCHEDULED
                category = "scheduled"
                break
              case 7: // POSTED
                category = "live"
                break
              default:
                // Skip other statuses
                return
            }

            if (category) {
              const campaign = {
                id: topic.id,
                campaignName: topic.name || topic.campaignName || "Untitled Campaign",
                text: firstPost.postText || "",
                mediaUrl:
                  firstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                  firstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                  null,
                socialNetworkType:
                  firstPost.channel?.socialNetworkType || firstPost.socialNetworkType?.description || null,
                socialNetworkName: firstPost.channel?.name || "",
                event: topic.event
                  ? {
                      name: topic.event.name || "",
                      category: topic.event.category || "",
                      url: topic.event.url || "",
                      date: topic.event.date || "",
                    }
                  : null,
                pillar: topic.contentPillar?.description || "",
                status: category,
                contentTopicPosts: topic.contentTopicPosts || [],
                postDatetime: firstPost.postDatetime,
                brandToneScore: firstPost.brandToneScore || 0,
                firstPost,
                originalTopic: topic,
              }

              processedTopics[category].push(campaign)
              statusCounts[category]++
            }
          }
        })

        state.processedTopics = processedTopics
        state.statusCounts = statusCounts
      })
      .addCase(fetchPublisherTopics.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch publisher topics"
      })
      .addCase(deleteTopic.fulfilled, (state, action) => {
        const { postId } = action.payload

        const allTabs = ["draft", "scheduled", "live"]
        let postFound = false

        for (const tabType of allTabs) {
          const campaigns = state.processedTopics[tabType]

          for (let i = 0; i < campaigns.length; i++) {
            const campaign = campaigns[i]
            const postIndex = campaign.contentTopicPosts.findIndex((p) => p.id == postId)

            if (postIndex !== -1) {
              postFound = true
              const updatedPosts = campaign.contentTopicPosts.filter((p) => p.id != postId)

              // If no posts left, remove the entire campaign
              if (updatedPosts.length === 0) {
                state.processedTopics = {
                  ...state.processedTopics,
                  [tabType]: campaigns.filter((c) => c.id !== campaign.id),
                }
                state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
              } else {
                const newFirstPost = updatedPosts[0]
                state.processedTopics = {
                  ...state.processedTopics,
                  [tabType]: campaigns.map((c) => {
                    if (c.id === campaign.id) {
                      return {
                        ...c,
                        contentTopicPosts: updatedPosts,
                        firstPost: newFirstPost,
                        text: newFirstPost.postText || "",
                        mediaUrl:
                          newFirstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                          newFirstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                          null,
                      }
                    }
                    return c
                  }),
                }
              }
              return
            }
          }
        }

        if (!postFound) {
          console.error("Post not found in publisher slice:", postId)
        }
      })
  },
})

export const {
  clearPublisherTopics,
  clearPublisherError,
  resetPublisherOnLogout,
  movePostToTab,
  removePostFromCampaign,
} = publisherSlice.actions

export default publisherSlice.reducer
